import { IconLogo } from '@components/icons/IconLogo';
import useEditingStore from '@hooks/useEditingStore';
import useLangString from '@hooks/useLangString';
import CategoryWidget from '@modules/player/SceneWidgets/CategoryWidget';
import { CustomButtonWidget } from '@modules/player/SceneWidgets/CustomButtonWidget';
import { $curScene, $tourData } from '@stores/dataStores';
import {
  $editingCategoryConfig,
  $editingCustomButton,
  $editingFlag,
  $editingLangFlag,
  $editingLogo,
  $editingUIColor,
} from '@stores/editingStores';
import { $isCn } from '@stores/langStores';
import { $panel } from '@stores/panelStores';
import { PanelType, SceneType, TemplateId } from '@types';
import { getTemplateConfig } from '@utils/templateConfig';
import { useStore, useStoreMap } from 'effector-react';
import { Scrollbars } from 'react-custom-scrollbars';
import WLogo from '../../WLogo';
import useNickname from '../../useNickname';
import { TProjectIcon } from '../TProjectIcon';

export function TProjectPanel() {
  const panelType = useStore($panel).type;
  const categoryConfig = useStore($editingCategoryConfig.store);
  const flag = useStore($editingFlag.store);
  const ui = useStore($editingUIColor.store);
  const logo = useStore($editingLogo.store);
  const btns = useStore($editingCustomButton.store);
  const categoryConfigEditing = useEditingStore({
    $data: $tourData,
    dataFn: (state) => state.categoryConfig,
    $editing: $editingCategoryConfig.store,
  });

  // 只在全局功能打开时显示, 互动功能下不显示
  const flagEditing = panelType === PanelType.globalSettings && !!flag;

  if (!categoryConfig && !ui && !flagEditing && !logo && !btns) return null;
  if (!categoryConfigEditing?.enabledButton) return null;
  return <TProjectPanelContent />;
}

function TProjectPanelContent() {
  const isMatrix = useStore($curScene)?.type === SceneType.matrix;
  const { name } = useNickname();
  const title = useStoreMap({
    store: $tourData,
    keys: [],
    fn: (state) => state.name,
  });
  const isCn = useStore($isCn);
  const ui = useEditingStore({
    $data: $tourData,
    dataFn: (state) => {
      const ui = state.config?.ui;
      const defaultUI = getTemplateConfig(TemplateId.project)('uiColor') ?? {};
      if (!ui) return defaultUI;
      return { ...defaultUI, ...ui };
    },
    $editing: $editingUIColor.store,
  });
  const flag = useEditingStore({
    $data: $tourData,
    dataFn: (state) => state.flag,
    $editing: $editingFlag.store,
  });
  const langFlag = useEditingStore({
    $data: $tourData,
    dataFn: (state) => state.config?.language?.showButton,
    $editing: $editingLangFlag.store,
  });

  const lang = useLangString(
    {
      author: ['创作者：', 'Author: '],
      pv: ['～人来过', '～ views'],
    },
    []
  );

  const showName = !!(flag?.showAuthorName ?? 1);
  const showPv = !!(flag?.showTourPV ?? 1);

  const showFS = !!(flag?.showFullscreenBtn ?? 1);
  const showVR = !!(flag?.showVRBtn ?? 1) && !isMatrix;
  const showVS = !!(flag?.showViewSwitchBtn ?? 1) && !isMatrix;
  const showLang = !!(langFlag ?? 0);
  const showHotspotBtn = !!(flag?.showHotspotBtn ?? 0);

  const showProjectName = !!(flag?.showSceneName ?? 1);

  return (
    <div
      className="absolute top-0 left-0 bottom-0 flex w-300 flex-col"
      style={{
        backgroundColor: `rgba(${ui?.panelBgColor})`,
      }}
    >
      <div
        className="p-15"
        style={{
          color: `rgba(${ui?.fontColor})`,
        }}
      >
        <div className="flex items-center h-50">
          <WLogo
            className="max-h-[50px]"
            systemIcon={<IconLogo color={`rgba(${ui.fontColor})`} />}
          />
          <TProjectIcon className="ml-auto">
            {({ color }) => (
              <>
                <defs>
                  <clipPath id="prefixj2v9dTDpW92erXtf1G8Nga">
                    <rect width="24" height="24" rx="0" />
                  </clipPath>
                </defs>
                <g clipPath="url(#prefixj2v9dTDpW92erXtf1G8Nga)">
                  <path
                    fill={color}
                    d="m12 10.587 4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.415 4.95-4.95-4.95-4.95L7.05 5.638l4.95 4.95Z"
                  />
                </g>
              </>
            )}
          </TProjectIcon>
        </div>
        {showProjectName && (
          <div
            className="mt-20 break-all text-[20px] font-bold leading-[26px]"
            title={title}
          >
            {title}
          </div>
        )}
        {(showName || showPv) && (
          <div className="mt-10 flex items-center justify-between">
            {showName && (
              <div
                className="mr-20 max-w-[180px] truncate"
                title={name}
              >{`${lang.author}${name}`}</div>
            )}
            {showPv && <div>{lang.pv}</div>}
          </div>
        )}
      </div>
      <div className="flex-1">
        <Scrollbars>
          <CustomButtonWidget />
          <CategoryWidget />
        </Scrollbars>
      </div>
      <div
        className="ga flex h-55 shrink-0 items-center gap-20 px-15"
        style={{
          backgroundColor: `rgba(${ui?.bgColor})`,
        }}
      >
        {showFS && (
          <TProjectIcon>
            {({ color }) => (
              <path
                fill={color}
                fillRule="evenodd"
                d="m15.605 4.303 4.434-.55a.185.185 0 0 1 .209.208l-.549 4.432a.186.186 0 0 1-.316.11L18.1 7.221l-3.6 3.6a.188.188 0 0 1-.265 0l-1.057-1.057a.188.188 0 0 1 0-.265l3.598-3.597-1.282-1.282a.187.187 0 0 1 .11-.317ZM9.499 13.18a.188.188 0 0 1 .265 0l1.06 1.055a.188.188 0 0 1 0 .264l-3.6 3.6 1.281 1.282a.187.187 0 0 1-.11.317l-4.434.55a.187.187 0 0 1-.209-.208l.551-4.432a.186.186 0 0 1 .317-.11l1.282 1.282 3.597-3.6Z"
              />
            )}
          </TProjectIcon>
        )}
        {showHotspotBtn && (
          <TProjectIcon>
            {({ color }) => (
              <path
                fill={color}
                d="M12 20a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-7.072-2.344a1.002 1.002 0 0 1 1.415 0 1 1 0 1 1-1.415 0Zm14.143.001a.999.999 0 1 1-1.414 1.414.999.999 0 1 1 1.414-1.414ZM12 8a4 4 0 1 1 0 8 4 4 0 0 1 0-8Zm9 3a1 1 0 1 1 0 2 1 1 0 0 1 0-2ZM3 11a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm16.071-6.071a1 1 0 1 1-1.413 1.415 1 1 0 0 1 1.413-1.415Zm-14.142 0a.999.999 0 1 1 1.414 1.414.999.999 0 1 1-1.414-1.414ZM12 2a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"
              />
            )}
          </TProjectIcon>
        )}
        {showVR && (
          <TProjectIcon>
            {({ color }) => (
              <path
                fill={color}
                fillRule="evenodd"
                d="M22 5H2v13h7v-1.086l2.854-2.853 3.12 3.121.026-.025V18h7V5Zm-9.854 6.525-.292.293-.293-.293-4.243 4.243.232.232H4V7h16v9h-3.843l.232-.232-4.242-4.243ZM7.5 12a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm9 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"
              />
            )}
          </TProjectIcon>
        )}
        {showVS && (
          <TProjectIcon>
            {({ color }) => (
              <path
                fill={color}
                fillRule="evenodd"
                d="M21.11 2.929c-.735-.733-2.204-1.418-5.045-.391a.992.992 0 0 0-.539 1.27c.196.49.735.734 1.225.587 1.42-.488 2.497-.537 2.987-.097.539.537.392 1.955-.392 3.715a24.553 24.553 0 0 1-4.8 6.6 23.852 23.852 0 0 1-6.611 4.79c-1.763.783-3.184.93-3.722.392-.539-.538-.392-1.956.392-3.716 0 0 0-.048.049-.097.03-.03.023-.042.013-.058-.006-.01-.013-.021-.013-.04v-.489c0-.049 0-.049-.05-.098-1.763-4.057.099-8.8 4.164-10.56 1.028-.44 2.106-.684 3.183-.684.44 0 .882.05 1.322.098.54.098 1.078-.293 1.176-.831.098-.538-.294-1.076-.833-1.173A9.034 9.034 0 0 0 12 2c-5.534 0-9.991 4.449-9.991 9.924 0 1.271.245 2.493.685 3.667-.98 2.444-.93 4.4.196 5.524.637.635 1.568.929 2.45.88a8.394 8.394 0 0 0 3.085-.733c5.142 2.004 10.921-.587 12.88-5.72a10.094 10.094 0 0 0 0-7.187c.98-2.395.931-4.302-.195-5.426ZM7.837 8.649a.446.446 0 0 1-.392.195.53.53 0 0 1-.343-.147c-.196-.146-.245-.488-.049-.684.294-.293.54-.587.833-.831a6.935 6.935 0 0 1 3.82-1.662c.245 0 .49.195.539.44a.573.573 0 0 1-.44.538c-1.225.195-2.352.684-3.282 1.466-.245.196-.49.44-.686.685Zm-1.714.83c.098-.244.392-.341.637-.244.244.147.342.391.293.587l-.147.293c-.097.245-.146.49-.244.734-.05.195-.245.342-.49.342h-.147a.434.434 0 0 1-.343-.587c.049-.293.196-.538.294-.831l.147-.293Zm6.597 10.364c-.302 0-.604-.043-.905-.086 1.595-.99 3.06-2.194 4.396-3.528 1.336-1.333 2.543-2.796 3.535-4.388.517 3.829-2.155 7.4-6.035 7.916-.301.086-.646.086-.991.086Z"
              />
            )}
          </TProjectIcon>
        )}
        {showLang && isCn && (
          <TProjectIcon>
            {({ color }) => (
              <path
                fill={color}
                fillRule="evenodd"
                d="M10.904 15.227V17H3V7h7.767v1.864H5.284V11h5.117v1.864H5.284v2.363h5.62Zm3.289-3.727V17h-2.147V7h2.467l3.837 6.136c.03.061.076.137.137.228.213.333.35.59.411.772v-.045c-.03-.333-.045-.833-.045-1.5V7H21v10h-2.33l-3.975-6.273-.548-.909c.03.424.046.985.046 1.682Z"
              />
            )}
          </TProjectIcon>
        )}
        {showLang && !isCn && (
          <TProjectIcon>
            {({ color }) => (
              <path
                fill={color}
                fillRule="evenodd"
                d="M11.598 13.696Q10.794 17 7.06 17C3.844 16.853 2.158 15.18 2 11.978Q2.236 7.264 7.06 7q3.403.088 4.444 2.952l-2.27.484Q8.761 8.938 7.059 8.85c-1.607.088-2.459 1.13-2.553 3.128.063 2.026.914 3.098 2.553 3.216q1.939-.044 2.27-2.159l2.27.661Zm3.357-2.203v5.33h-2.222v-9.69h2.553l3.972 5.946q.047.088.142.22c.22.324.362.573.425.75v-.045a16.34 16.34 0 0 1-.047-1.453V7.132H22v9.692h-2.411l-4.114-6.08-.567-.88q.047.616.047 1.63Z"
              />
            )}
          </TProjectIcon>
        )}
      </div>
    </div>
  );
}
